import { useState, useEffect } from "react";
import { supabase } from "./supabase";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

export default function FeeTracker({ academy }) {
  const [players, setPlayers] = useState([]);
  const [feeRecords, setFeeRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeBatch, setActiveBatch] = useState("All");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear] = useState(new Date().getFullYear());
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");

  const batches = ["All", "Morning", "Evening", "Weekend", "U-14", "U-19"];

  const [userId, setUserId] = useState(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => setUserId(user?.id));
  }, []);

  useEffect(() => { if (userId) fetchData(); }, [selectedMonth, selectedYear, userId]);

  const fetchData = async () => {
    setLoading(true);
    const [{ data: playerData }, { data: feeData }] = await Promise.all([
      supabase.from("players").select("*").eq("user_id", userId).order("name"),
      supabase.from("fee_records").select("*").eq("month", selectedMonth + 1).eq("year", selectedYear),
    ]);
    setPlayers(playerData || []);
    setFeeRecords(feeData || []);
    setLoading(false);
  };

  const getFeeRecord = (playerId) => feeRecords.find((r) => r.player_id === playerId);
  const isPaid = (playerId) => getFeeRecord(playerId)?.status === "paid";

  const markPaid = async (player, method) => {
    setSaving(true);
    const existing = getFeeRecord(player.id);
    if (existing) {
      await supabase.from("fee_records").update({
        status: "paid", payment_method: method,
        paid_date: new Date().toISOString().split("T")[0],
      }).eq("id", existing.id);
    } else {
      await supabase.from("fee_records").insert([{
        player_id: player.id, month: selectedMonth + 1, year: selectedYear,
        amount: player.fee_amount, status: "paid", payment_method: method,
        paid_date: new Date().toISOString().split("T")[0], user_id: userId,
      }]);
    }
    await fetchData();
    setSaving(false);
    setSelectedPlayer(null);
    showToast(`✅ ${player.name} marked as paid`);
  };

  const markUnpaid = async (player) => {
    const existing = getFeeRecord(player.id);
    if (existing) {
      await supabase.from("fee_records").delete().eq("id", existing.id);
      await fetchData();
      showToast(`↩ ${player.name} marked as unpaid`);
    }
    setSelectedPlayer(null);
  };

  const openWhatsApp = (player) => {
    if (!player.whatsapp) {
      showToast("No WhatsApp number saved for this player");
      return;
    }
    const num = player.whatsapp.replace(/\s/g, "");
    const msg = encodeURIComponent(
      `Hi ${player.parent_name || ""},  this is a reminder that ${player.name}'s academy fee of ₹${player.fee_amount} for ${MONTHS[selectedMonth]} is due. Please pay at your earliest. - The Coach Room`
    );
    window.open(`https://wa.me/91${num}?text=${msg}`, "_blank");
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const filtered = players.filter((p) => activeBatch === "All" || p.batch === activeBatch);
  const paid = filtered.filter((p) => isPaid(p.id));
  const unpaid = filtered.filter((p) => !isPaid(p.id));
  const totalFees = filtered.reduce((sum, p) => sum + (Number(p.fee_amount) || 0), 0);
  const collectedFees = paid.reduce((sum, p) => sum + (Number(p.fee_amount) || 0), 0);
  const pct = totalFees > 0 ? Math.round((collectedFees / totalFees) * 100) : 0;

  const avatarColors = ["#1a3a1b","#2d6a4f","#1a472a","#0d3321","#2c5f2e","#40916c"];
  const getColor = (name) => avatarColors[(name?.charCodeAt(0) || 0) % avatarColors.length];
  const getInitials = (name) => name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "?";

  return (
    <div style={s.container}>

      {/* HEADER */}
      <div style={s.header}>
        <div style={s.logoRow}>
          <div style={s.logoIcon}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="#d4a017" strokeWidth="1.5" strokeLinejoin="round"/>
              <path d="M2 17l10 5 10-5" stroke="#d4a017" strokeWidth="1.5" strokeLinejoin="round"/>
              <path d="M2 12l10 5 10-5" stroke="#d4a017" strokeWidth="1.5" strokeLinejoin="round"/>
            </svg>
          </div>
          <span style={s.logoText}>{academy?.name?.toUpperCase() || "THE COACH ROOM"}</span>
        </div>
        <div style={s.headerBottom}>
          <div>
            <h2 style={s.title}>Fee Tracker</h2>
            <p style={s.sub}>{MONTHS[selectedMonth]} {selectedYear}</p>
          </div>
        </div>

        {/* MONTH SELECTOR */}
        <div style={s.monthRow}>
          {MONTHS.map((m, i) => (
            <button key={m} style={{ ...s.monthChip, background: selectedMonth === i ? "#d4a017" : "rgba(255,255,255,0.08)", color: selectedMonth === i ? "#1a3a1b" : "rgba(255,255,255,0.5)", fontWeight: selectedMonth === i ? "800" : "400" }} onClick={() => setSelectedMonth(i)}>
              {m.slice(0, 3)}
            </button>
          ))}
        </div>
      </div>

      {/* SUMMARY */}
      <div style={s.summaryRow}>
        <div style={s.summaryCard}>
          <div style={{ ...s.summaryNum, color: "#38a169" }}>{paid.length}</div>
          <div style={s.summaryLabel}>Paid</div>
        </div>
        <div style={s.summaryCard}>
          <div style={{ ...s.summaryNum, color: "#e53e3e" }}>{unpaid.length}</div>
          <div style={s.summaryLabel}>Unpaid</div>
        </div>
        <div style={{ ...s.summaryCard, borderRight: "none" }}>
          <div style={{ ...s.summaryNum, color: "#1a3a1b", fontSize: "1rem" }}>
            ₹{collectedFees.toLocaleString("en-IN")}
          </div>
          <div style={s.summaryLabel}>Collected</div>
        </div>
      </div>

      {/* PROGRESS BAR */}
      <div style={s.progressWrap}>
        <div style={s.progressTrack}>
          <div style={{ ...s.progressFill, width: `${pct}%` }} />
        </div>
        <span style={s.progressPct}>{pct}% collected</span>
      </div>

      {/* BATCH FILTER */}
      <div style={s.batchRow}>
        {batches.map((b) => (
          <button key={b} style={{ ...s.batchChip, background: activeBatch === b ? "#1a3a1b" : "white", color: activeBatch === b ? "#d4a017" : "#6b7280", border: activeBatch === b ? "1.5px solid #1a3a1b" : "1.5px solid #e8e0d0", fontWeight: activeBatch === b ? "700" : "400" }} onClick={() => setActiveBatch(b)}>{b}</button>
        ))}
      </div>

      {/* LIST */}
      <div style={s.list}>
        {loading ? (
          <div style={s.empty}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={s.empty}>No players found</div>
        ) : (
          <>
            {/* UNPAID */}
            {unpaid.length > 0 && (
              <>
                <div style={s.sectionLabel}>
                  <span style={{ ...s.dot, background: "#e53e3e" }} />
                  Unpaid · {unpaid.length}
                </div>
                {unpaid.map((player) => (
                  <div key={player.id} style={{ ...s.card, borderLeftColor: "#e53e3e" }}>
                    <div style={{ ...s.avatar, background: getColor(player.name) }}>{getInitials(player.name)}</div>
                    <div style={s.info}>
                      <div style={s.name}>{player.name}</div>
                      <div style={s.meta}>{player.batch} · {player.student_id}</div>
                    </div>
                    <div style={s.cardRight}>
                      <div style={s.amount}>₹{Number(player.fee_amount || 0).toLocaleString("en-IN")}</div>
                      <div style={s.actions}>
                        {player.whatsapp && (
                          <button style={s.waBtn} onClick={() => openWhatsApp(player)} title="Send WhatsApp reminder">
                            💬
                          </button>
                        )}
                        <button style={s.paidBtn} onClick={() => setSelectedPlayer(player)}>
                          ✓ Paid
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* PAID */}
            {paid.length > 0 && (
              <>
                <div style={s.sectionLabel}>
                  <span style={{ ...s.dot, background: "#38a169" }} />
                  Paid · {paid.length}
                </div>
                {paid.map((player) => {
                  const record = getFeeRecord(player.id);
                  return (
                    <div key={player.id} style={{ ...s.card, borderLeftColor: "#38a169", background: "#f0fdf4" }}>
                      <div style={{ ...s.avatar, background: "#38a169" }}>{getInitials(player.name)}</div>
                      <div style={s.info}>
                        <div style={s.name}>{player.name}</div>
                        <div style={s.meta}>
                          {record?.payment_method && `${record.payment_method} · `}
                          {record?.paid_date && new Date(record.paid_date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                        </div>
                      </div>
                      <div style={s.cardRight}>
                        <div style={{ ...s.amount, color: "#38a169" }}>₹{Number(player.fee_amount || 0).toLocaleString("en-IN")}</div>
                        <button style={s.undoBtn} onClick={() => { setSelectedPlayer(player); }}>
                          Undo
                        </button>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}
      </div>

      {/* MODAL */}
      {selectedPlayer && (
        <div style={s.overlay} onClick={() => setSelectedPlayer(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.handle} />
            <div style={s.modalName}>{selectedPlayer.name}</div>
            <div style={s.modalSub}>{selectedPlayer.batch} · {selectedPlayer.student_id}</div>
            <div style={s.modalAmount}>
              <div style={s.modalAmountNum}>₹{Number(selectedPlayer.fee_amount || 0).toLocaleString("en-IN")}</div>
              <div style={s.modalAmountLabel}>{MONTHS[selectedMonth]} {selectedYear}</div>
            </div>
            <div style={s.modalActions}>
              {!isPaid(selectedPlayer.id) ? (
                <>
                  <button style={{ ...s.modalBtn, background: "#1a3a1b", color: "white" }} onClick={() => markPaid(selectedPlayer, "Cash")} disabled={saving}>💵 Cash Paid</button>
                  <button style={{ ...s.modalBtn, background: "#6366f1", color: "white" }} onClick={() => markPaid(selectedPlayer, "UPI")} disabled={saving}>📱 UPI Paid</button>
                  {selectedPlayer.whatsapp && (
                    <button style={{ ...s.modalBtn, background: "#25D366", color: "white" }} onClick={() => { openWhatsApp(selectedPlayer); setSelectedPlayer(null); }}>💬 Send WhatsApp Reminder</button>
                  )}
                </>
              ) : (
                <button style={{ ...s.modalBtn, background: "#fde8de", color: "#9b1c1c" }} onClick={() => markUnpaid(selectedPlayer)} disabled={saving}>↩ Mark as Unpaid</button>
              )}
              <button style={{ ...s.modalBtn, background: "#f0ebe0", color: "#6b7280" }} onClick={() => setSelectedPlayer(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {toast && <div style={s.toast}>{toast}</div>}
    </div>
  );
}

const s = {
  container: { flex: 1, overflowY: "auto", background: "#f0ebe0", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", minHeight: "100%", position: "relative" },
  header: { background: "#111a12", padding: "1.4rem 1.5rem 1rem" },
  logoRow: { display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "1.2rem" },
  logoIcon: { width: "32px", height: "32px", background: "rgba(212,160,23,0.15)", border: "1px solid rgba(212,160,23,0.4)", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center" },
  logoText: { color: "white", fontSize: "0.72rem", fontWeight: "800", letterSpacing: "3px" },
  headerBottom: { marginBottom: "1rem" },
  title: { color: "white", fontSize: "1.5rem", fontWeight: "700", margin: 0, letterSpacing: "-0.3px" },
  sub: { color: "rgba(255,255,255,0.4)", fontSize: "0.78rem", margin: "0.2rem 0 0", fontWeight: "300" },
  monthRow: { display: "flex", gap: "0.4rem", overflowX: "auto", scrollbarWidth: "none", paddingBottom: "0.5rem" },
  monthChip: { padding: "0.3rem 0.6rem", fontSize: "0.72rem", border: "none", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", borderRadius: "4px", transition: "all 0.15s" },
  summaryRow: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", background: "white", borderBottom: "1px solid #e8e0d0" },
  summaryCard: { padding: "0.9rem 0.5rem", textAlign: "center", borderRight: "1px solid #e8e0d0" },
  summaryNum: { fontWeight: "800", fontSize: "1.5rem", lineHeight: 1 },
  summaryLabel: { fontSize: "0.6rem", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", marginTop: "0.2rem", fontWeight: "500" },
  progressWrap: { display: "flex", alignItems: "center", gap: "0.8rem", padding: "0.7rem 1.2rem", background: "white", borderBottom: "1px solid #e8e0d0" },
  progressTrack: { flex: 1, height: "6px", background: "#e8e0d0", borderRadius: "3px", overflow: "hidden" },
  progressFill: { height: "100%", background: "linear-gradient(90deg, #1a3a1b, #38a169)", borderRadius: "3px", transition: "width 0.8s ease" },
  progressPct: { fontSize: "0.75rem", fontWeight: "700", color: "#1a3a1b", whiteSpace: "nowrap" },
  batchRow: { display: "flex", gap: "0.5rem", padding: "0.7rem 1.2rem", overflowX: "auto", scrollbarWidth: "none" },
  batchChip: { padding: "0.35rem 0.8rem", fontSize: "0.75rem", cursor: "pointer", whiteSpace: "nowrap", letterSpacing: "0.3px", transition: "all 0.15s", flexShrink: 0, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
  list: { padding: "0 1.2rem 5rem" },
  sectionLabel: { display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.68rem", fontWeight: "800", textTransform: "uppercase", letterSpacing: "1px", color: "#6b7280", padding: "1rem 0 0.4rem" },
  dot: { width: "8px", height: "8px", borderRadius: "50%", display: "inline-block" },
  card: { background: "white", border: "1px solid #e8e0d0", borderLeft: "3px solid", padding: "0.8rem 0.9rem", marginBottom: "0.5rem", display: "flex", alignItems: "center", gap: "0.8rem" },
  avatar: { width: "38px", height: "38px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: "0.78rem", fontWeight: "800", flexShrink: 0 },
  info: { flex: 1, minWidth: 0 },
  name: { fontSize: "0.88rem", fontWeight: "700", color: "#1a1a1a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  meta: { fontSize: "0.7rem", color: "#9ca3af", marginTop: "0.2rem" },
  cardRight: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.4rem", flexShrink: 0 },
  amount: { fontSize: "0.9rem", fontWeight: "800", color: "#1a3a1b" },
  actions: { display: "flex", gap: "0.4rem" },
  waBtn: { background: "#d4f8e0", border: "none", padding: "0.3rem 0.5rem", fontSize: "0.85rem", cursor: "pointer", borderRadius: "4px" },
  paidBtn: { background: "#1a3a1b", color: "#d4a017", border: "none", padding: "0.3rem 0.7rem", fontSize: "0.68rem", fontWeight: "700", cursor: "pointer", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
  undoBtn: { background: "none", border: "1px solid #e8e0d0", color: "#9ca3af", padding: "0.25rem 0.6rem", fontSize: "0.68rem", fontWeight: "600", cursor: "pointer", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
  empty: { textAlign: "center", padding: "3rem", color: "#6b7280", fontSize: "0.9rem" },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-end", zIndex: 50 },
  modal: { background: "white", borderRadius: "20px 20px 0 0", padding: "1.2rem 1.5rem 2rem", width: "100%", maxWidth: "480px", margin: "0 auto" },
  handle: { width: "40px", height: "4px", background: "#e8e0d0", borderRadius: "2px", margin: "0 auto 1.2rem" },
  modalName: { fontSize: "1.1rem", fontWeight: "800", color: "#1a1a1a", marginBottom: "0.2rem" },
  modalSub: { fontSize: "0.78rem", color: "#6b7280", marginBottom: "1rem" },
  modalAmount: { background: "#f0ebe0", padding: "1rem", textAlign: "center", marginBottom: "1.2rem" },
  modalAmountNum: { fontWeight: "800", fontSize: "2rem", color: "#1a3a1b", lineHeight: 1 },
  modalAmountLabel: { fontSize: "0.75rem", color: "#6b7280", marginTop: "0.3rem" },
  modalActions: { display: "flex", flexDirection: "column", gap: "0.6rem" },
  modalBtn: { width: "100%", padding: "0.9rem", border: "none", fontSize: "0.88rem", fontWeight: "700", cursor: "pointer", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", letterSpacing: "0.3px" },
  toast: { position: "fixed", bottom: "5rem", left: "50%", transform: "translateX(-50%)", background: "#1a3a1b", color: "white", padding: "0.7rem 1.4rem", borderRadius: "20px", fontSize: "0.82rem", fontWeight: "600", whiteSpace: "nowrap", zIndex: 100, boxShadow: "0 4px 20px rgba(0,0,0,0.2)" },
};